import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import BikeModal from '../components/BikeModal';

export default function Home() {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedBike, setSelectedBike] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    async function fetchBikes() {
      try {
        const resp = await fetch(`${API_BASE}/app/bikes`);
        if (!resp.ok) {
          throw new Error('Failed to fetch bikes');
        }
        const data = await resp.json();
        setBikes(data);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchBikes();
  }, []);

  const openDetails = (bike) => {
    setSelectedBike(bike);
    setShowModal(true);
  };

  const closeDetails = () => {
    setSelectedBike(null);
    setShowModal(false);
  };

  // Filter listings
  const newBikes = bikes.filter(b => b.category?.toLowerCase() === 'new').slice(0, 4);
  const oldBikes = bikes.filter(b => b.category?.toLowerCase() === 'old').slice(0, 4);
  const offers = bikes.filter(b => b.category?.toLowerCase() === 'offer');

  return (
    <>
      <div className="hero">
        <div className="container text-center text-md-start">
          <div className="row align-items-center g-lg-5">
            <div className="col-lg-7 pe-lg-5">
              <h1>
                Ride with <span className="highlight">Shivam Moto</span> <br /> House of Multibrand
              </h1>
              <p className="lead mb-5 opacity-75">
                India's most trusted bike destination | New, Used & Exchange | 25+ years of legacy
              </p>
              <div className="d-flex flex-wrap gap-3">
                <Link to="/new-bikes" className="btn btn-shine px-5">
                  <i className="fas fa-motorcycle me-2"></i>View Available Bikes
                </Link>
                <Link to="/exchange" className="btn btn-outline-dark rounded-pill px-5 shadow-sm">
                  Exchange Now →
                </Link>
              </div>
            </div>
            <div className="col-lg-5 mt-4 mt-lg-0">
              <video 
                autoPlay 
                muted 
                loop 
                playsInline 
                className="img-fluid rounded-4 shadow-lg"
                style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
              >
                <source src="/bike rotate video.mp4?v=1" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>

      {/* WHY CHOOSE US */}
      <div className="container py-5 my-5">
        <div className="text-center mb-5 pb-3">
          <span className="section-title">Why Choose Us</span>
        </div>
        <div className="row g-4">
          <div className="col-md-3">
            <div className="why-card">
              <i className="fas fa-shield-alt"></i>
              <h5 className="mt-3 fw-bold">Trusted Brand</h5>
              <p>1 Million+ happy riders</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="why-card">
              <i className="fas fa-calendar-check"></i>
              <h5 className="mt-3 fw-bold">Serving Since 2000</h5>
              <p>25+ years of excellence</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="why-card">
              <i className="fas fa-star"></i>
              <h5 className="mt-3 fw-bold">4.9★ Google Rating</h5>
              <p>Top rated dealership</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="why-card">
              <i className="fas fa-handshake"></i>
              <h5 className="mt-3 fw-bold">Hassle Free Exchange</h5>
              <p>Instant valuation</p>
            </div>
          </div>
        </div>
      </div>

      {/* BRANDS WE SERVE */}
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 section-header-mobile">
          <h3 className="fw-bold">Brands We Serve</h3>
          <Link to="/new-bikes" className="text-decoration-none text-warning fw-semibold">
            View All <i className="fas fa-arrow-right"></i>
          </Link>
        </div>
        <div className="row text-center g-4">
          <div className="col-3 col-md-2">
            <i className="fas fa-motorcycle fa-3x text-secondary"></i>
            <p className="mt-2 fw-semibold">Hero</p>
          </div>
          <div className="col-3 col-md-2">
            <i className="fas fa-bolt fa-3x text-secondary"></i>
            <p className="mt-2 fw-semibold">Suzuki</p>
          </div>
          <div className="col-3 col-md-2">
            <i className="fas fa-shield-alt fa-3x text-secondary"></i>
            <p className="mt-2 fw-semibold">Honda</p>
          </div>
          <div className="col-3 col-md-2">
            <i className="fas fa-tachometer-alt fa-3x text-secondary"></i>
            <p className="mt-2 fw-semibold">Bajaj</p>
          </div>
          <div className="col-3 col-md-2">
            <i className="fab fa-yammer fa-3x text-secondary"></i>
            <p className="mt-2 fw-semibold">Yamaha</p>
          </div>
          <div className="col-3 col-md-2">
            <i className="fas fa-charging-station fa-3x text-secondary"></i>
            <p className="mt-2 fw-semibold">TVS</p>
          </div>
        </div>
      </div>

      {/* EXCLUSIVE OFFERS */}
      {offers.length > 0 && (
        <div className="container py-5" id="offers-wrapper">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 section-header-mobile">
            <h2 className="fw-bold" style={{ color: '#e67e22' }}>
              🎉 Today's Exclusive Offers
            </h2>
          </div>
          <div className="row g-4" id="offers-container">
            {offers.map(bike => {
              const isSold = bike.status === 'sold';
              const imageUrl = bike.image_url
                ? (bike.image_url.startsWith('http') ? bike.image_url : `${API_BASE}/${bike.image_url}`)
                : 'https://via.placeholder.com/300x200?text=No+Image';

              return (
                <div className="col-md-6 col-lg-4" key={bike.id}>
                  <div className="bike-card card h-100 border-warning border-2" style={{ background: '#fffaf0' }}>
                    <div className="sold-container">
                      <img 
                        src={imageUrl} 
                        className={`card-img-top ${isSold ? 'sold-img' : ''}`} 
                        alt={bike.title} 
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; }}
                      />
                      {isSold && <div className="sold-badge-overlay">Sold Out</div>}
                    </div>
                    <div className="card-body">
                      <span className="badge bg-danger rounded-pill px-3 py-2 mb-2">
                        🔥 {bike.badge || 'Limited Time Offer'}
                      </span>
                      <h4 className="fw-bold mt-2">{bike.title}</h4>
                      <p className="price text-danger">{bike.price}</p>
                      {bike.info && <p className="fw-semibold mb-1">{bike.info}</p>}
                      {bike.description ? (
                        <>
                          <p 
                            className="small text-muted mb-2" 
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {bike.description}
                          </p>
                          <button 
                            className="btn btn-link btn-sm p-0 mb-3 text-warning text-decoration-none fw-bold"
                            onClick={() => openDetails(bike)}
                          >
                            Read More
                          </button>
                        </>
                      ) : (
                        <div className="mb-3"></div>
                      )}
                      {isSold ? (
                        <button className="btn btn-secondary rounded-pill w-100 fw-bold disabled">Sold Out</button>
                      ) : (
                        <a 
                          href={`https://api.whatsapp.com/send?phone=919876543210&text=Hi,%20I%20am%20interested%20in%20your%20offer:%20${encodeURIComponent(bike.title)}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn btn-warning rounded-pill w-100 fw-bold"
                        >
                          Claim Offer
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NEW ARRIVALS */}
      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 section-header-mobile">
          <h2 className="fw-bold">Fresh Arrivals – New Bikes</h2>
          <Link to="/new-bikes" className="btn btn-outline-warning rounded-pill">
            View All New Bikes →
          </Link>
        </div>
        <div className="row g-4">
          {loading ? (
            <div className="text-center w-100 py-3">
              <div className="spinner-border text-dark"></div>
            </div>
          ) : error ? (
            <div className="text-center w-100 py-3 text-danger">Error loading new bikes.</div>
          ) : newBikes.length === 0 ? (
            <p className="text-muted col-12">No items listed yet.</p>
          ) : (
            newBikes.map(bike => {
              const isSold = bike.status === 'sold';
              const imageUrl = bike.image_url
                ? (bike.image_url.startsWith('http') ? bike.image_url : `${API_BASE}/${bike.image_url}`)
                : 'https://via.placeholder.com/300x200?text=No+Image';

              return (
                <div className="col-md-6 col-lg-3" key={bike.id}>
                  <div className="bike-card card h-100">
                    <div className="sold-container">
                      <img 
                        src={imageUrl} 
                        className={`card-img-top ${isSold ? 'sold-img' : ''}`} 
                        alt={bike.title}
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; }}
                      />
                      {isSold && <div className="sold-badge-overlay">Sold Out</div>}
                    </div>
                    <div className="card-body">
                      {bike.badge && <span className="badge-bike mb-2">{bike.badge}</span>}
                      <h5 className={`fw-bold ${bike.badge ? 'mt-2' : ''}`}>{bike.title}</h5>
                      <p className="price">{bike.price}</p>
                      {bike.info && <p className="mb-1 fw-semibold text-muted small">{bike.info}</p>}
                      {bike.description ? (
                        <>
                          <p 
                            className="small text-muted mb-1" 
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {bike.description}
                          </p>
                          <button 
                            className="btn btn-link btn-sm p-0 mb-3 text-warning text-decoration-none fw-bold"
                            onClick={() => openDetails(bike)}
                          >
                            Read More
                          </button>
                        </>
                      ) : (
                        <div className="mb-3"></div>
                      )}
                      {isSold ? (
                        <button className="btn btn-secondary btn-sm rounded-pill w-100 mb-2 disabled">Sold Out</button>
                      ) : (
                        <a 
                          href={`https://api.whatsapp.com/send?phone=919876543210&text=${encodeURIComponent('Hi, I am interested in ' + bike.title + ' (' + bike.price + '). ' + (bike.info ? 'Specs: ' + bike.info + '. ' : '') + (bike.description ? 'Details: ' + bike.description : ''))}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn btn-outline-warning btn-sm rounded-pill w-100 mb-2"
                        >
                          Enquire Now
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* USED BIKES */}
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
          <h2 className="fw-bold">Pre Owned – Used Bikes</h2>
          <Link to="/old-bikes" className="btn btn-outline-warning rounded-pill">
            View All Used Bikes →
          </Link>
        </div>
        <div className="row g-4">
          {loading ? (
            <div className="text-center w-100 py-3">
              <div className="spinner-border text-dark"></div>
            </div>
          ) : error ? (
            <div className="text-center w-100 py-3 text-danger">Error loading pre-owned bikes.</div>
          ) : oldBikes.length === 0 ? (
            <p className="text-muted col-12">No items listed yet.</p>
          ) : (
            oldBikes.map(bike => {
              const isSold = bike.status === 'sold';
              const imageUrl = bike.image_url
                ? (bike.image_url.startsWith('http') ? bike.image_url : `${API_BASE}/${bike.image_url}`)
                : 'https://via.placeholder.com/300x200?text=No+Image';

              return (
                <div className="col-md-6 col-lg-3" key={bike.id}>
                  <div className="bike-card card h-100">
                    <div className="sold-container">
                      <img 
                        src={imageUrl} 
                        className={`card-img-top ${isSold ? 'sold-img' : ''}`} 
                        alt={bike.title}
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found'; }}
                      />
                      {isSold && <div className="sold-badge-overlay">Sold Out</div>}
                    </div>
                    <div className="card-body">
                      {bike.badge && <span className="badge bg-secondary text-white mb-2">{bike.badge}</span>}
                      <h5 className={`fw-bold ${bike.badge ? 'mt-2' : ''}`}>{bike.title}</h5>
                      <p className="price">{bike.price}</p>
                      {bike.info && <p className="mb-1 fw-semibold text-muted small">{bike.info}</p>}
                      {bike.description ? (
                        <>
                          <p 
                            className="small text-muted mb-1" 
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {bike.description}
                          </p>
                          <button 
                            className="btn btn-link btn-sm p-0 mb-3 text-warning text-decoration-none fw-bold"
                            onClick={() => openDetails(bike)}
                          >
                            Read More
                          </button>
                        </>
                      ) : (
                        <div className="mb-3"></div>
                      )}
                      {isSold ? (
                        <button className="btn btn-secondary btn-sm rounded-pill w-100 mb-2 disabled">Sold Out</button>
                      ) : (
                        <a 
                          href={`https://api.whatsapp.com/send?phone=919876543210&text=${encodeURIComponent('Hi, I am interested in ' + bike.title + ' (' + bike.price + '). ' + (bike.info ? 'Specs: ' + bike.info + '. ' : '') + (bike.description ? 'Details: ' + bike.description : ''))}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn btn-outline-dark btn-sm rounded-pill w-100 mb-2"
                        >
                          Enquire Now
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* EXCHANGE BOX */}
      <div className="container py-5 my-5">
        <div className="exchange-box text-center">
          <h2 className="fw-bold">💱 Sell or Exchange Your Bike</h2>
          <p className="fs-5 mt-2">Get the best value for your bike. Instant price, zero paperwork, free inspection.</p>
          <Link to="/exchange" className="btn btn-dark btn-lg rounded-pill px-5 mt-3">
            <i className="fas fa-exchange-alt me-2"></i>Sell / Exchange Now
          </Link>
        </div>
      </div>

      {/* TESTIMONIALS */}
      <div className="container py-4 mb-4">
        <div className="text-center">
          <h2 className="section-title"> What Our Customers Say</h2>
          <p className="mt-3 fs-5">Rated 4.8/5 on Google • 1,200+ reviews</p>
        </div>
        <div className="row mt-4 g-4">
          <div className="col-md-4">
            <div className="p-4 bg-white rounded-4 shadow-sm">
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star-half-alt text-warning"></i>
              <p className="mt-2 fst-italic">"Excellent service! Got my dream bike at the best price."</p>
              <h6 className="fw-bold">— Raj Kumar</h6>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-4 bg-white rounded-4 shadow-sm">
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <p className="mt-2">"Very professional and trustworthy. Best dealership in town."</p>
              <h6 className="fw-bold">— Priya S</h6>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-4 bg-white rounded-4 shadow-sm">
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <i className="fas fa-star text-warning"></i>
              <p className="mt-2">"Great collection, staff very helpful and transparent."</p>
              <h6 className="fw-bold">— Arun M</h6>
            </div>
          </div>
        </div>
      </div>

      {/* BIKE DETAILS MODAL */}
      <BikeModal bike={selectedBike} show={showModal} onClose={closeDetails} />
    </>
  );
}
